import type { Status } from '@servicetim/shared';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ProblemBanner,
} from '@servicetim/ui/components';
import { useEffect, useId, useState } from 'react';
import { fieldErrorsOf, problemDetail } from '../../projects/lib/field-errors';
import { SimpleSelect } from '../../projects/ui/simple-select';

export interface MissingMapping {
  statusId: string;
  /** Parsed from the API message ("12 tickets are in this status; …"); null when absent. */
  ticketCount: number | null;
  message: string;
}

export const STATUS_MAPPING_REQUIRED = 'workflow.status_mapping_required';

/**
 * Reads a 409 `workflow.status_mapping_required` problem: one entry per
 * `errors[].path = statusMapping.<removedStatusId>`. `null` for any other error.
 */
export function parseStatusMappingError(error: unknown): MissingMapping[] | null {
  if (problemDetail(error) !== STATUS_MAPPING_REQUIRED) return null;
  return fieldErrorsOf(error)
    .filter((e) => e.path.startsWith('statusMapping.'))
    .map((e) => {
      const count = /^(\d+)\s/.exec(e.message)?.[1];
      return {
        statusId: e.path.slice('statusMapping.'.length),
        ticketCount: count ? Number(count) : null,
        message: e.message,
      };
    });
}

export interface StatusMappingDialogProps {
  missing: MissingMapping[] | null;
  /** Names of the removed statuses (from the last published snapshot); ids shown otherwise. */
  statusNames: Record<string, string>;
  /** Active statuses of the draft, the only valid targets. */
  targets: readonly Status[];
  onSubmit: (mapping: Record<string, string>) => Promise<void>;
  onCancel: () => void;
  busy?: boolean;
  error?: unknown;
}

/** Publishing removed statuses that still hold tickets: pick where each one's tickets go. */
export function StatusMappingDialog({
  missing,
  statusNames,
  targets,
  onSubmit,
  onCancel,
  busy,
  error,
}: StatusMappingDialogProps) {
  const id = useId();
  const [mapping, setMapping] = useState<Record<string, string>>({});
  // biome-ignore lint/correctness/useExhaustiveDependencies: handlers and mutation resets are stable for the component lifetime
  useEffect(() => {
    setMapping({});
  }, [missing]);
  const open = missing !== null && missing.length > 0;
  const complete = open && missing.every((m) => mapping[m.statusId]);
  const total = (missing ?? []).reduce((n, m) => n + (m.ticketCount ?? 0), 0);

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onCancel())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Move tickets out of removed statuses</DialogTitle>
          <DialogDescription>
            The draft removes {missing?.length ?? 0} status{missing?.length === 1 ? '' : 'es'} that
            still hold{total > 0 ? ` ${total}` : ''} tickets. Choose a target for each; a background
            job moves the tickets after publishing.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (complete) void onSubmit(mapping);
          }}
        >
          {error && parseStatusMappingError(error) === null ? (
            <ProblemBanner error={error} size="sm" />
          ) : null}
          <ul className="space-y-3">
            {(missing ?? []).map((m) => (
              <li
                key={m.statusId}
                className="grid gap-2 sm:grid-cols-[1fr_auto_1fr] sm:items-center"
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium text-fg">
                    {statusNames[m.statusId] ?? (
                      <code className="font-mono text-xs">{m.statusId.slice(0, 8)}…</code>
                    )}
                  </span>
                  {m.ticketCount !== null ? (
                    <Badge variant="warning">
                      {m.ticketCount} ticket{m.ticketCount === 1 ? '' : 's'}
                    </Badge>
                  ) : null}
                </div>
                <span className="text-fg-muted text-sm" aria-hidden="true">
                  →
                </span>
                <SimpleSelect
                  id={`${id}-${m.statusId}`}
                  aria-label={`Target for ${statusNames[m.statusId] ?? m.statusId}`}
                  value={mapping[m.statusId]}
                  onChange={(next) =>
                    setMapping((prev) => {
                      const copy = { ...prev };
                      if (next) copy[m.statusId] = next;
                      else delete copy[m.statusId];
                      return copy;
                    })
                  }
                  options={targets.map((s) => ({ value: s.id, label: s.name }))}
                  placeholder="Choose a status…"
                />
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={!complete || busy}>
              Publish and move tickets
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
